import React, { type PropsWithChildren, useState, forwardRef } from 'react';

import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/glyph/editor/edit';
import Spinner from '@atlaskit/spinner';
import { B100, N20A, R500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { visuallyHiddenStyles } from '@atlassian/jira-accessibility';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	type ReadViewContainerProps,
	type ReadViewEditButtonProps,
	EditButtonHidden,
	EditButton,
} from '@atlassian/jira-inline-edit-lite';
import type { Props } from './types';

type PropsOld = PropsWithChildren<{
	/**
	 * Props to be spread onto the read views container element to support accessible click interactions. This will be
	 * `undefined` when rendered in a 'read-only' layout outside of an `InlineEditLite` component.
	 */
	containerProps?: ReadViewContainerProps;
	/**
	 * Props to be spread onto the read view edit button element to support accessible click interactions. This will be
	 * `undefined` when rendered in a 'read-only' layout outside of an `InlineEditLite` component.
	 */
	editButtonProps?: ReadViewEditButtonProps;
	/**
	 * When `true` this will set the min-height of the read view container to 100%, allowing the element to grow to
	 * the height of the containing block.
	 * This is useful for fixed height layouts where the click area should fill the entire ancestor containing block,
	 * e.g. when rendered within the `<td/>` of a table.
	 */
	readViewFitContainerHeight?: boolean;
	/**
	 * When `false` a visible edit button will be rendered after the provided children when the container is
	 * hovered/focused. This defaults to `true` which means the edit button will remain hidden at all times but is still
	 * accessible for keyboard/screen reader navigation.
	 */
	hideEditButton?: boolean;
	/**
	 * When `true` this will display a loading overlay and spinner. This is typically used as the loading fallback when
	 * an async edit view is rendered within the `InlineEditLite` component.
	 */
	isLoading?: boolean;
	/**
	 * When `true` this will display a red border around the container. This is typically used if an async error has
	 * occurred after an inline edit field has been optimistically updated, to help inform the user that the update was
	 * unsuccessful. This scenario should be accompanied by an appropriate flag in the UI.
	 */
	isInvalid?: boolean;
}>;

/**
 * This component is designed to be rendered within the `readView` of an `InlineEditLite` component. This can also be
 * rendered independently in a 'read-only' layout by omitting the `containerProps` and `editButtonProps` arguments,
 * which is suitable for consumers who want to enforce consistent spacing when a field is conditionally editable.
 */
export const InlineEditReadViewContainerOld = forwardRef<HTMLDivElement, PropsOld>(
	(
		{
			containerProps,
			editButtonProps,
			readViewFitContainerHeight = false,
			hideEditButton = true,
			isLoading = false,
			isInvalid = false,
			children,
		},
		ref,
	) => {
		const [isHiddenButtonFocused, setIsHiddenButtonFocused] = useState(false);
		// Button focus could be implemented more cleanly with :has pseudo-selector once supported in Firefox (currently in
		// preview https://developer.mozilla.org/en-US/docs/Web/CSS/:has#browser_compatibility)
		const isFocusRingVisible = isHiddenButtonFocused || isLoading;

		let buttonBefore = null;
		let buttonAfter = null;

		if (editButtonProps) {
			if (hideEditButton) {
				const { label, ref: editButtonRef, ...rest } = editButtonProps;
				buttonBefore = (
					<HiddenButton
						{...rest}
						ref={(buttonRef) => {
							editButtonRef.current = buttonRef;
						}}
						aria-label={label}
						onFocus={() => setIsHiddenButtonFocused(true)}
						onBlur={() => setIsHiddenButtonFocused(false)}
					/>
				);
			} else {
				const { label, ...rest } = editButtonProps;
				buttonAfter = (
					<Button
						{...rest}
						appearance="subtle"
						iconBefore={<EditIcon label={label} primaryColor={token('color.icon', '#44546F')} />}
						spacing="none"
						// In future providing CSS variables *may* be something that makes its way into the design
						// system, but for now this is an acceptable override to prevent managing this state with JS
						// given styles are co-located. More context here https://atlassian.slack.com/archives/C060GTVV988/p1703214250865779?thread_ts=1703212675.166419&cid=C060GTVV988
						/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
						style={editButtonStyles}
						interactionName="auto-generated.jira-issue-field-inline-edit-read-view-container.ui.button"
					/>
				);
			}
		}

		return (
			<Container
				{...containerProps}
				data-testid="issue-field-inline-edit-read-view-container.ui.container"
				readViewFitContainerHeight={readViewFitContainerHeight}
				hideEditButton={hideEditButton}
				isEditable={!!editButtonProps}
				isFocusRingVisible={isFocusRingVisible}
				isInvalid={isInvalid}
				ref={ref}
			>
				{buttonBefore}
				{isLoading ? (
					<>
						<LoadingOverlay>{children}</LoadingOverlay>
						<SpinnerContainer>
							<Spinner />
						</SpinnerContainer>
					</>
				) : (
					children
				)}
				{buttonAfter}
			</Container>
		);
	},
);

/**
 * This component is designed to be rendered within the `readView` of a `FieldInlineEditLite` component. This can be
 * rendered in a 'read-only' layout by omitting the `context` prop which is provided by `useInlineEdit` in
 * `@atlassian/jira-inline-edit-lite`. This ensures consistent spacing for consumers when a field is conditionally
 * editable.
 */
const InlineEditReadViewContainer = forwardRef<HTMLDivElement, Props>(
	(
		{
			children,
			context,
			editButtonLabel,
			editButtonRef,
			hideEditButton = true,
			isEditButtonFocused = false,
			isLoading = false,
			isInvalid = false,
			onClick,
			onMouseDown,
			onEditButtonBlur,
			onEditButtonFocus,
			readViewFitContainerHeight = false,
		},
		ref,
	) => {
		const isHiddenButtonFocused = hideEditButton && isEditButtonFocused;
		const isFocusRingVisible = isHiddenButtonFocused || isLoading;

		return (
			<Container
				data-testid="issue-field-inline-edit-read-view-container.ui.container"
				readViewFitContainerHeight={readViewFitContainerHeight}
				hideEditButton={hideEditButton}
				isEditable={!!context}
				isFocusRingVisible={isFocusRingVisible}
				isInvalid={isInvalid}
				onClick={onClick}
				onMouseDown={onMouseDown}
				role="presentation"
				ref={ref}
			>
				{hideEditButton && context && (
					<EditButtonHidden
						ref={editButtonRef}
						context={context}
						label={editButtonLabel}
						onBlur={onEditButtonBlur}
						onFocus={onEditButtonFocus}
					/>
				)}
				{isLoading ? (
					<>
						<LoadingOverlay>{children}</LoadingOverlay>
						<SpinnerContainer>
							<Spinner />
						</SpinnerContainer>
					</>
				) : (
					children
				)}
				{!hideEditButton && context && (
					<EditButton
						ref={editButtonRef}
						context={context}
						label={editButtonLabel}
						onBlur={onEditButtonBlur}
						onFocus={onEditButtonFocus}
						// In future providing CSS variables *may* be something that makes its way into the design system, but
						// for now this is an acceptable override to prevent managing additional state with JS.
						// More context here https://atlassian.slack.com/archives/C060GTVV988/p1703214250865779?thread_ts=1703212675.166419&cid=C060GTVV988
						/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
						style={editButtonStyles}
					/>
				)}
			</Container>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	readViewFitContainerHeight: boolean;
	hideEditButton: boolean;
	isEditable: boolean;
	isFocusRingVisible: boolean;
	isInvalid: boolean;
}>(
	{
		borderWidth: '2px',
		borderStyle: 'solid',
		borderRadius: token('border.radius', '3px'),
		padding: `0 ${token('space.075', '6px')}`,
		display: 'flex',
		gap: token('space.100', '8px'),
		alignItems: 'center',
		boxSizing: 'border-box',
		maxWidth: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		minHeight: (props) => (props.readViewFitContainerHeight ? '100%' : '32px'),
		position: 'relative',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.hideEditButton &&
		!props.isEditable && {
			paddingRight: `calc(${token('space.400', '32px')} + ${token('space.075', '6px')})`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isEditable && {
			transition: 'backgroundColor 0.2s',
			'&:hover': {
				backgroundColor: token('color.background.neutral.subtle.hovered', N20A),
			},
			'--edit-button-opacity': 0,
			'&:hover,&:focus-within': {
				'--edit-button-opacity': 1,
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isFocusRingVisible && {
			borderColor: token('color.border.focused', B100),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isInvalid &&
		!props.isFocusRingVisible && {
			borderColor: token('color.border.danger', R500),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.isInvalid &&
		!props.isFocusRingVisible && {
			borderColor: 'transparent',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	() => ff('nin-resizable-columns_8z4eo') && { overflow: 'hidden', textOverflow: 'ellipsis' },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const HiddenButton = styled.button(visuallyHiddenStyles);

const editButtonStyles = {
	flexShrink: 0,
	marginLeft: 'auto',
	opacity: 'var(--edit-button-opacity, 1)',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingOverlay = styled.div({
	pointerEvents: 'none',
	opacity: token('opacity.loading', '0.2'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	position: 'absolute',
	// Space tokens do not make sense for this positioning.
	left: '50%',
	top: '50%',
	transform: 'translate(-50%, -50%)',
});

export default InlineEditReadViewContainer;
